import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import styles from './NotificationsPage.module.scss'

import { fetchAPI } from '@/api'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import Input from '@/components/Input'
import { Switch } from '@/components/Switch'
import { selectIsInternalAdmin } from '@/store/features/user/userSlice'

const notificationSchema = z
  .object({
    type: z.string().min(1, 'Notification type is required'),
    key: z.string().min(1, 'Notification key is required'),
    title: z.string().min(1, 'Title is required'),
    text: z.string().min(1, 'Text is required'),
    emails: z.string().optional(),
    sendToAll: z.boolean().optional(),
  })
  .refine(
    (data) => {
      // If sending to all, we don't need emails
      if (data.sendToAll) {
        return true
      }
      // If not sending to all, we need at least one valid email
      if (!data.sendToAll && (!data.emails || data.emails.trim() === '')) {
        return false
      }
      // If emails are provided, they should be valid
      if (data.emails) {
        const emailList = data.emails
          .split(',')
          .map((email) => email.trim())
          .filter((email) => email.length > 0)
        if (emailList.length === 0) return false
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailList.every((email) => emailRegex.test(email))
      }
      return true
    },
    {
      message: "Please enter at least one valid email address or toggle 'Send to all users'",
      path: ['emails'],
    }
  )

type NotificationFormValues = z.infer<typeof notificationSchema>

const NotificationsPage = () => {
  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const [typeItems, setTypeItems] = useState<DropdownItem[]>([
    {
      id: 'achievement',
      title: 'Achievement',
      selected: false,
    },
    {
      id: 'referral',
      title: 'Referral',
      selected: false,
    },
    {
      id: 'announcement',
      title: 'Announcement',
      selected: false,
    },
  ])

  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<NotificationFormValues | null>(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<NotificationFormValues>({
    resolver: zodResolver(notificationSchema),
    defaultValues: {
      type: '',
      key: '',
      title: '',
      text: '',
      emails: '',
      sendToAll: false,
    },
  })

  const watchSendToAll = watch('sendToAll')

  const onSubmit = async (data: NotificationFormValues) => {
    setSuccess(null)

    // Show confirmation dialog for sending to all users
    if (data.sendToAll) {
      setPendingFormData(data)
      setShowConfirmDialog(true)
      return
    }

    // For specific users, proceed directly
    await sendNotification(data)
  }

  const sendNotification = async (data: NotificationFormValues) => {
    setIsLoading(true)

    try {
      if (data.sendToAll) {
        // Use announce endpoint for all users
        // Auth'd by user status - internal (@wispr.ai) admin users only
        await fetchAPI({
          method: 'POST',
          url: '/api/v1/notification/internal/announce',
          data: {
            key: data.key,
            title: data.title,
            text: data.text,
            type: data.type,
          },
        })
      } else {
        // Use create endpoint for specific users
        // Auth'd by user status - internal (@wispr.ai) admin users only
        const notificationData = {
          type: data.type,
          key: data.key,
          title: data.title,
          text: data.text,
        }

        const emails = data.emails
          ? data.emails
              .split(',')
              .map((email) => email.trim())
              .filter((email) => email.length > 0)
          : []

        await fetchAPI({
          method: 'POST',
          url: '/api/v1/notification/internal/create',
          data: { emails, notification: notificationData },
        })
      }

      setSuccess('Notifications sent successfully')
      reset() // Clear form fields on success
    } catch (err) {
      console.error(err)
      setSuccess('Error: Failed to send notifications')
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirmSend = async () => {
    setShowConfirmDialog(false)
    if (pendingFormData) {
      await sendNotification(pendingFormData)
      setPendingFormData(null)
    }
  }

  const handleCancelSend = () => {
    setShowConfirmDialog(false)
    setPendingFormData(null)
  }

  // Authorization: only internal (@wispr.ai) admin users can access this page
  if (!isInternalAdmin) {
    return (
      <div className={styles.container}>
        <p>You are not authorized to access this page</p>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Send Notification to Flow Users</h1>

      {/* Confirmation Dialog */}
      <DialogWrapper
        isOpen={showConfirmDialog}
        title="Confirm Send to All Users"
        subtitle="You are about to send a notification to every user in the system"
        close={handleCancelSend}
        minWidth={540}
      >
        <div className={styles.dialogContent}>
          {pendingFormData && (
            <div className={styles.dialogDetailsSection}>
              <p className={styles.dialogSectionTitle}>Notification Details</p>
              <div className={styles.dialogNotificationDetails}>
                <p className={styles.dialogDetailItem}>
                  <strong>Type:</strong>
                  <span>{pendingFormData.type}</span>
                </p>
                <p className={styles.dialogDetailItem}>
                  <strong>Key:</strong>
                  <span>{pendingFormData.key}</span>
                </p>
                <p className={styles.dialogDetailItem}>
                  <strong>Title:</strong>
                  <span>{pendingFormData.title}</span>
                </p>
                <p className={styles.dialogDetailItem}>
                  <strong>Text:</strong>
                  <span>{pendingFormData.text}</span>
                </p>
              </div>
              <div className={styles.dialogWarningMessage}>
                <span className={styles.warningIcon}>⚠️</span>
                <span>
                  <strong>This action cannot be undone.</strong> The notification will be sent to all users immediately.
                </span>
              </div>
            </div>
          )}
          <div className={styles.dialogActions}>
            <Button title="Cancel" onClick={handleCancelSend} type="light" />
            <Button
              title="Send to All Users"
              onClick={handleConfirmSend}
              type="dark"
              isLoading={isLoading}
              isDisabled={isLoading}
            />
          </div>
        </div>
      </DialogWrapper>

      <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Required Fields</h2>

          <div className={styles.formGroup}>
            <label className={styles.label}>Notification Type *</label>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Dropdown
                  items={typeItems}
                  setItems={setTypeItems}
                  onSelect={(items) => {
                    field.onChange(items[0]?.id || '')
                    setTypeItems((prev) =>
                      prev.map((item) => ({
                        ...item,
                        selected: item.id === items[0]?.id,
                      }))
                    )
                  }}
                  placeholder="Select type"
                  className={styles.dropdown}
                />
              )}
            />
            {errors.type && <p className={styles.errorText}>{errors.type.message}</p>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Key * <span className={styles.helperText}>(e.g., &apos;1000_words&apos;, &apos;120_wpm&apos;)</span>
            </label>
            <Controller
              name="key"
              control={control}
              render={({ field }) => (
                <Input
                  type="text"
                  value={field.value}
                  onInput={field.onChange}
                  placeholder="Notification identifier"
                  className={styles.input}
                />
              )}
            />
            {errors.key && <p className={styles.errorText}>{errors.key.message}</p>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Title *</label>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input
                  type="text"
                  value={field.value}
                  onInput={field.onChange}
                  placeholder="Notification title"
                  className={styles.input}
                />
              )}
            />
            {errors.title && <p className={styles.errorText}>{errors.title.message}</p>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Text *</label>
            <Controller
              name="text"
              control={control}
              render={({ field }) => (
                <textarea
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="Notification text"
                  className={styles.textarea}
                  rows={4}
                />
              )}
            />
            {errors.text && <p className={styles.errorText}>{errors.text.message}</p>}
          </div>

          <div className={styles.formGroup}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px',
              }}
            >
              <Controller
                name="sendToAll"
                control={control}
                render={({ field }) => <Switch checked={field.value || false} onInputChange={field.onChange} />}
              />
              <label className={styles.label}>Send to all users</label>
            </div>
            <span className={styles.helperText}>
              {watchSendToAll
                ? 'Notification will be sent to all users in the system'
                : 'Toggle this to send to all users instead of specific emails'}
            </span>
            {watchSendToAll && (
              <div className={styles.warningMessage}>
                ⚠️ <strong>Caution:</strong> This will send the notification to every user in the system. Please
                double-check your message content and ensure this is intentional before sending.
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Emails <span className={styles.helperText}>(comma separated)</span>
            </label>
            <Controller
              name="emails"
              control={control}
              render={({ field }) => (
                <Input
                  type="text"
                  value={field.value || ''}
                  onInput={field.onChange}
                  placeholder={
                    watchSendToAll
                      ? 'Email input disabled - sending to all users'
                      : 'user@example.com, user2@example.com'
                  }
                  className={styles.input}
                  isDisabled={watchSendToAll}
                />
              )}
            />
            {!watchSendToAll && errors.emails && <p className={styles.errorText}>{errors.emails.message}</p>}
          </div>
        </div>

        <div className={styles.actionContainer}>
          {success && <div className={styles.success}>{success}</div>}

          <Button
            title="Send Notification"
            onClick={handleSubmit(onSubmit)}
            type="dark"
            isLoading={isLoading}
            isDisabled={isLoading}
          />
        </div>
      </form>
    </div>
  )
}

export default NotificationsPage
